// The "My kind of day" editor, as step 2 of the lens menu.
//
// It used to be a popover anchored under a chip. It is now the second step of
// the picker that reaches it — same surface, entered from "My kind of day ›" and
// left by "‹ All ratings" — which is what makes its depth free: nothing is on
// screen until it is asked for, and the way back is always the same words.
//
// The editor is a view onto the recipe, not a second model of it. Every control
// writes through setRecipe and then re-reads the clamped result, so what is on
// screen is always what is in force.
//
// The one substantive change to what it ASKS: the four spoiler weights are an
// order rather than four sliders. "Does dryness matter more than calm" is a
// question people can answer; "how much does dryness matter, 0 to 100" is not.
// lib/recipe-ranks.ts translates back, so lib/recipe.ts, the ?r= wire string and
// the server's cache key are all untouched.

import { $ } from '../dom.ts';
import { state } from '../state.ts';
import { PRESETS } from '../../lib/recipe.ts';
import { weightsFromRanks, ranksFromWeights } from '../../lib/recipe-ranks.ts';
import { VAR } from '../../lib/variables.ts';
import type { Gate, Recipe, WeightKey, Weights } from '../../lib/recipe.ts';
import type { VarKey } from '../../lib/variables.ts';

export interface RecipeEditorDeps {
  setRecipe: (r: Recipe, opts?: { defer?: boolean }) => void;
}

let deps: RecipeEditorDeps;

export function initRecipeEditor(d: RecipeEditorDeps): void {
  deps = d;
}

// The gate changes the units, so it changes every slider's range.
const GATE_UI: Record<Gate, { min: number; max: number; step: number; tolMin: number; tolMax: number; unit: string; word: string; other: string }> = {
  app: { min: -30, max: 45, step: 1, tolMin: 1, tolMax: 15, unit: '°C', word: 'feels-like', other: 'or gate it on wind instead' },
  ws: { min: 0, max: 25, step: 0.5, tolMin: 0.5, tolMax: 10, unit: 'm/s', word: 'wind', other: 'or gate it on how it feels instead' },
};
// Switching gate swaps the trap into that gate's units; the matching preset's
// plateau is the only sensible starting point there.
const GATE_TRAP_DEFAULT: Record<Gate, number[]> = { app: PRESETS.hike.trap, ws: PRESETS.sail.trap };

// The tint each rank carries. The order is legible as a gradient before any
// numeral is read, which is what lets the numerals stay 12px and quiet.
const RANK_TINTS = [0.2, 0.14, 0.055, 0.05];
const tintFor = (i: number) => `rgba(63, 191, 133, ${RANK_TINTS[i] ?? 0.05})`;

// What each spoiler is called in the ranking. Short: a chip is small and four of
// them share a row.
const WEIGHT_LABEL: Record<WeightKey, string> = {
  dry: 'Dry',
  calm: 'Calm',
  sunny: 'Sunny sky',
  clear: 'Clear air',
  warm: 'Warm enough',
};

// The four the editor ranks. clampRecipe forces exactly one of warm/calm to zero
// because it contradicts the gate, so there are only ever four — which is why
// the ranking is a faithful mapping and not a lossy one.
function visibleWeights(gate: Gate): WeightKey[] {
  return gate === 'ws' ? ['dry', 'sunny', 'clear', 'warm'] : ['dry', 'calm', 'sunny', 'clear'];
}

// The order currently on screen. Read from the DOM rather than kept in a
// variable, so a drag has exactly one source of truth mid-gesture.
function orderFromDom(): WeightKey[] {
  return [...document.querySelectorAll<HTMLElement>('#ce-ranks .ce-rank')].map((el) => el.dataset.k as WeightKey);
}

// Editor -> recipe. The plateau feet are symmetric (b−tol, c+tol); clampRecipe
// normalises everything else downstream.
function recipeFromEditor(): Recipe {
  const gate = ($('ce-gate-btn').dataset.gate as Gate) || 'app';
  const b = Number($<HTMLInputElement>('ce-b').value);
  const c = Number($<HTMLInputElement>('ce-c').value);
  const tol = Number($<HTMLInputElement>('ce-tol').value);
  const order = orderFromDom();
  const w = order.length ? weightsFromRanks(order, state.recipe.w) : ({ ...state.recipe.w } as Weights);
  return { gate, trap: [b - tol, b, c, c + tol], w };
}

function commit({ defer = false }: { defer?: boolean } = {}): void {
  deps.setRecipe(recipeFromEditor(), { defer });
  syncCustomEditor();
}

// ── the ranking ─────────────────────────────────────────────────────────────
function renderRanks(order: WeightKey[]): void {
  $('ce-ranks').replaceChildren(
    ...order.map((k, i) => {
      const chip = document.createElement('div');
      chip.className = 'ce-rank';
      chip.dataset.k = k;
      chip.draggable = true;
      chip.tabIndex = 0;
      chip.style.setProperty('--rank-tint', tintFor(i));
      chip.setAttribute('role', 'listitem');
      chip.setAttribute('aria-label', `${WEIGHT_LABEL[k]}, ranked ${i + 1} of ${order.length}. Arrow keys reorder.`);
      const n = document.createElement('span');
      n.className = 'ce-rank-n';
      n.textContent = String(i + 1);
      const label = document.createElement('span');
      label.textContent = WEIGHT_LABEL[k];
      chip.append(n, label);
      return chip;
    }),
  );
}

// Renumber and re-tint in place. A full re-render mid-drag would destroy the
// element being dragged and end the gesture.
function restampRanks(): void {
  $('ce-ranks').querySelectorAll<HTMLElement>('.ce-rank').forEach((el, i) => {
    el.querySelector('.ce-rank-n')!.textContent = String(i + 1);
    el.style.setProperty('--rank-tint', tintFor(i));
  });
}

// Arrow keys do what the drag does. A drag-only control would be the one thing
// in this redesign a keyboard could not reach.
function moveRank(k: WeightKey, delta: number): void {
  const order = orderFromDom();
  const from = order.indexOf(k);
  const to = Math.min(order.length - 1, Math.max(0, from + delta));
  if (from < 0 || from === to) return;
  order.splice(to, 0, ...order.splice(from, 1));
  renderRanks(order);
  commit();
  document.querySelector<HTMLElement>(`#ce-ranks .ce-rank[data-k="${k}"]`)?.focus();
}

function wireRanks(): void {
  const box = $('ce-ranks');
  let dragging: HTMLElement | null = null;

  box.addEventListener('dragstart', (e) => {
    const chip = (e.target as HTMLElement).closest<HTMLElement>('.ce-rank');
    if (!chip) return;
    dragging = chip;
    chip.classList.add('dragging');
    // Firefox refuses to start a drag without data on the transfer.
    (e as DragEvent).dataTransfer?.setData('text/plain', chip.dataset.k || '');
  });
  box.addEventListener('dragend', () => {
    dragging?.classList.remove('dragging');
    dragging = null;
    commit();
  });
  // Reorder live under the pointer rather than on drop: the field, the ruler and
  // the results re-score as you drag, which is the point of doing it this way.
  box.addEventListener('dragover', (e) => {
    e.preventDefault();
    const over = (e.target as HTMLElement).closest<HTMLElement>('.ce-rank');
    if (!over || !dragging || over === dragging) return;
    const chips = [...box.querySelectorAll<HTMLElement>('.ce-rank')];
    const movingUp = chips.indexOf(dragging) > chips.indexOf(over);
    box.insertBefore(dragging, movingUp ? over : over.nextSibling);
    restampRanks();
    commit({ defer: true });
  });

  box.addEventListener('keydown', (e) => {
    const chip = (e.target as HTMLElement).closest<HTMLElement>('.ce-rank');
    if (!chip) return;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); moveRank(chip.dataset.k as WeightKey, -1); }
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); moveRank(chip.dataset.k as WeightKey, +1); }
  });
}

// ── recipe -> editor ────────────────────────────────────────────────────────
// Also runs after every edit: the steps line up with clampRecipe's quantization,
// so re-writing a slider's own value mid-drag is a no-op rather than a jump.
export function syncCustomEditor(): void {
  const r = state.recipe;
  const ui = GATE_UI[r.gate];

  const gateBtn = $('ce-gate-btn');
  gateBtn.dataset.gate = r.gate;
  gateBtn.textContent = `${ui.word} ▾`;
  $('ce-gate-swap').textContent = ui.other;

  const setRange = (id: string, min: number, max: number, val: number, out: string) => {
    const el = $<HTMLInputElement>(id);
    el.min = String(min);
    el.max = String(max);
    el.step = String(ui.step);
    el.value = String(val);
    $(`${id}-out`).textContent = out;
  };
  // A hand-written recipe's feet may be asymmetric; the tolerance slider shows
  // the wider side and any edit rewrites both feet symmetric.
  const tol = Math.min(ui.tolMax, Math.max(ui.tolMin, r.trap[1] - r.trap[0], r.trap[3] - r.trap[2]));
  setRange('ce-b', ui.min, ui.max, r.trap[1], `${r.trap[1]} ${ui.unit}`);
  setRange('ce-c', ui.min, ui.max, r.trap[2], `${r.trap[2]} ${ui.unit}`);
  setRange('ce-tol', ui.tolMin, ui.tolMax, tol, `±${tol} ${ui.unit}`);

  // Only re-render the ranking when the SET of keys changed (a gate swap) or it
  // has not been drawn yet — re-rendering on every slider tick would fight a
  // drag in progress.
  const want = visibleWeights(r.gate);
  const have = orderFromDom();
  if (have.length !== want.length || want.some((k) => !have.includes(k))) {
    renderRanks(ranksFromWeights(r.w, want));
  }
}

// ── "copy a recipe from X" ──────────────────────────────────────────────────
// Seeds the editor's fields from a ready-made rating. It does NOT leave custom
// mode: switching lens is what the sentence's word is for, and conflating the
// two is how the old chip row made "edit mine" and "show theirs" feel the same.
function renderCopyMenu(): void {
  const box = $('ce-copy-menu');
  box.replaceChildren(
    ...(Object.keys(PRESETS) as (keyof typeof PRESETS)[]).map((key) => {
      const label = VAR[key as VarKey]?.label || key;
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'row';
      b.append(Object.assign(document.createElement('span'), { textContent: label }));
      b.addEventListener('click', () => {
        deps.setRecipe(PRESETS[key]);
        $('ce-copy').textContent = `Copy a recipe from ${label} ▾`;
        box.hidden = true;
        // A preset brings its own weights, so the ranking is derived from them
        // rather than kept — exactly what ranksFromWeights exists for.
        renderRanks(ranksFromWeights(state.recipe.w, visibleWeights(state.recipe.gate)));
        syncCustomEditor();
      });
      return b;
    }),
  );
}

export function wireCustomEditor(): void {
  renderCopyMenu();
  wireRanks();

  const swapGate = () => {
    const gate: Gate = ($('ce-gate-btn').dataset.gate as Gate) === 'ws' ? 'app' : 'ws';
    deps.setRecipe({ gate, trap: GATE_TRAP_DEFAULT[gate].slice(), w: { ...state.recipe.w } });
    renderRanks(ranksFromWeights(state.recipe.w, visibleWeights(gate)));
    syncCustomEditor();
  };
  $('ce-gate-swap').addEventListener('click', swapGate);
  // The word the sentence points at has to do something when it is clicked.
  $('ce-gate-btn').addEventListener('click', swapGate);

  $('ce-copy').addEventListener('click', (e) => {
    e.stopPropagation();
    const box = $('ce-copy-menu');
    box.hidden = !box.hidden;
    $('ce-copy').setAttribute('aria-expanded', String(!box.hidden));
  });

  const onSlide = (e: Event) => {
    // Keep the plateau ordered while dragging: the slider being moved pushes the
    // other edge along rather than crossing it.
    const b = $<HTMLInputElement>('ce-b');
    const c = $<HTMLInputElement>('ce-c');
    if (e.target === b && Number(b.value) > Number(c.value)) c.value = b.value;
    if (e.target === c && Number(c.value) < Number(b.value)) b.value = c.value;
    commit({ defer: true }); // dots/calendar live, tiles debounced
  };
  for (const id of ['ce-b', 'ce-c', 'ce-tol']) $(id).addEventListener('input', onSlide);

  // The seeded day's fallback fields, revealed by "Change".
  $('sd-change').addEventListener('click', () => {
    const fields = $('sd-fields');
    fields.hidden = !fields.hidden;
    if (!fields.hidden) $('sd-where').focus();
  });
}

// The editor is a step of the lens menu now, so "open it" is a menu state
// change; renderLensMenu decides whether it is visible. This survives as the one
// call the openers still make, and all it owes them is fields that match the
// recipe by the time they are looked at.
export function toggleCustomEditor(): void {
  syncCustomEditor();
}
